import { formatCount, parseMoneyToCents, tt } from '@agos/shared'

/**
 * Per-franchise figures - `SAAS.md` §3.12.
 *
 * **`StatsPage.tsx` had no helper and no test**, which is why it drifted: the endpoint answers
 * fourteen fields and the table drew four. The two that matter were both dropped.
 *
 * **`error`.** `PlatformStatsService` catches an unreachable tenant database, puts the message on
 * the row, and leaves the figures at the zeros it initialised them to - deliberately, per its own
 * class note: *"a platform dashboard that goes blank because one tenant is down is a dashboard
 * nobody can use during an incident… Each row carries its own error instead."* The screen never read
 * it, so a franchise that is **down** rendered identically to one with **no sales** - and the page's
 * own comment that *"the ordinary case for this table is mostly zeros"* is what hid it.
 *
 * **`lastOrderAt`.** The field that tells *stopped* from *quiet*. On the owner's data
 * `pizzamajstro` reads 0 orders and 0,00 € over thirty days beside **2 462 customers**, because its
 * newest order is 2026-07-28. Every figure true, the row saying something false.
 */

/** The window the server's `orders_month` and `revenue_month` are counted over. */
export const WINDOW_DAYS = 30

export interface FranchiseStat {
  franchiseId: number
  slug: string
  name: string
  packageSlug: string | null
  isActive: boolean
  /**
   * The three shorter windows and the database size - `SAAS.md` §3.42.
   *
   * **All four were answered and none was read.** Legacy's `Stats.tsx` renders every one of them per
   * franchise - `Dnes`, `7 dní`, `Tržby dnes` and `DB` - so this was a missed port.
   *
   * **`revenueToday` beside `ordersToday` is the pairing that matters**, and it is FB-545's shape:
   * a count without its value is half a figure. `ordersWeek` is the window between today and the
   * month, and the only one of the three that shows a direction.
   *
   * On the owner's data all three read nought - 0 today, 0 this week, 0 this month, newest order
   * 2026-07-28 - which is the same condition FB-545 addressed with `lastOrderAt`.
   */
  ordersToday: number
  ordersWeek: number
  revenueTodayCents: number
  dbSizeBytes: number
  ordersMonth: number
  revenueMonthCents: number
  totalCustomers: number
  totalOrders: number
  lastOrderAt: string | null
  /**
   * Why this row could not be read, or null.
   *
   * **Not an absent field - a reported failure.** When it is set every figure on the row is the
   * initialised zero rather than a measurement, so nothing on it may be shown as a number.
   */
  error: string | null
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

function count(value: unknown): number {
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

export function franchiseStatsOf(payload: unknown): FranchiseStat[] {
  const wrapped = (payload as { franchises?: unknown } | undefined)?.franchises
  const rows = Array.isArray(payload) ? payload : Array.isArray(wrapped) ? wrapped : []
  return rows.flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) return []
    const row = entry as Record<string, unknown>
    const slug = text(row.slug)
    if (slug === null) return []
    return [
      {
        franchiseId: count(row.franchiseId),
        slug,
        name: text(row.name) ?? slug,
        packageSlug: text(row.packageSlug),
        isActive: row.isActive !== false,
        ordersToday: count(row.ordersToday),
        ordersWeek: count(row.ordersWeek),
        revenueTodayCents: parseMoneyToCents(row.revenueToday as string | number | null),
        dbSizeBytes: count(row.dbSizeBytes),
        ordersMonth: count(row.ordersMonth),
        revenueMonthCents: parseMoneyToCents(row.revenueMonth as string | number | null),
        totalCustomers: count(row.totalCustomers),
        totalOrders: count(row.totalOrders),
        lastOrderAt: text(row.lastOrderAt),
        error: text(row.error),
      },
    ]
  })
}

/** Whether this row's figures are measurements at all. */
export function isUnreachable(stat: FranchiseStat): boolean {
  return stat.error !== null
}

/**
 * What to show instead of the figures on a row that could not be read.
 *
 * **A zero the screen cannot vouch for is worse than no figure**, which is the whole finding: the
 * server reports the failure precisely so it is not read as a measurement.
 */
export function unreachableNote(stat: FranchiseStat): string {
  if (!isUnreachable(stat)) return ''
  return `${tt('Databáza prevádzky je nedostupná - čísla nižšie by boli nuly, nie merania.')}`
}

/** How long ago, in whole days, or null when nothing is known. */
export function daysSinceLastOrder(stat: FranchiseStat, now: Date): number | null {
  if (stat.lastOrderAt === null) return null
  const then = new Date(stat.lastOrderAt).getTime()
  if (!Number.isFinite(then)) return null
  return Math.floor((now.getTime() - then) / 86_400_000)
}

export type ActivityState = 'unreachable' | 'never-sold' | 'stale' | 'trading'

/**
 * What this row actually says, once `lastOrderAt` is read.
 *
 * - `never-sold` - no order has ever been placed, which is not the same as a quiet month.
 * - `stale` - the newest order is **older than the window**, so the zero is a stop rather than a
 *   lull. This is the state the owner's only trading franchise is in.
 * - `trading` - there is activity inside the window, whatever the count.
 */
export function activityState(stat: FranchiseStat, now: Date): ActivityState {
  if (isUnreachable(stat)) return 'unreachable'
  if (stat.lastOrderAt === null) return 'never-sold'
  const days = daysSinceLastOrder(stat, now)
  return days !== null && days > WINDOW_DAYS ? 'stale' : 'trading'
}

/**
 * The sentence that makes a zero legible.
 *
 * Said for every state rather than only the bad ones - a row with no note reads as a row nobody
 * checked.
 */
export function activityNote(stat: FranchiseStat, now: Date): string {
  const state = activityState(stat, now)
  if (state === 'unreachable') return unreachableNote(stat)
  if (state === 'never-sold') {
    return stat.totalCustomers > 0
      ? `${tt('Nikdy žiadna objednávka')} - ${formatCount(stat.totalCustomers)} ${tt('zákazníkov')}`
      : tt('Nikdy žiadna objednávka')
  }
  const days = daysSinceLastOrder(stat, now) ?? 0
  if (state === 'stale') {
    // **The case the owner's data is in.** 0 orders in the window with the newest order outside it
    // is a franchise that stopped, and the count alone cannot say so.
    return `${tt('Posledná objednávka pred')} ${formatCount(days)} ${tt('dňami - mimo okna, preto je mesiac nulový')}`
  }
  return `${tt('Posledná objednávka pred')} ${formatCount(days)} ${tt('dňami')}`
}

/** True when the figures are real and worth reading as figures. */
export function showsFigures(stat: FranchiseStat): boolean {
  return !isUnreachable(stat)
}

/**
 * Lifetime beside the window, because the two answer different questions.
 *
 * 10 788 orders ever and 0 this month is a franchise that stopped; 0 and 0 is one that never
 * started. The window alone cannot distinguish them.
 */
export function lifetimeNote(stat: FranchiseStat): string {
  if (isUnreachable(stat)) return ''
  if (stat.totalOrders === 0) return tt('Za celú dobu žiadna objednávka')
  return `${formatCount(stat.totalOrders)} ${tt('objednávok za celú dobu')}`
}

/** The name `StatsPage` imports the row type under, so the table's generic reads as the row. */
export type FranchiseStats = FranchiseStat

export interface StatsTotals {
  ordersMonth: number
  revenueMonthCents: number
  totalCustomers: number
  /** How many rows were left out, because their figures are not measurements - §3.92. */
  unreadable: number
}

/**
 * The platform's own figures, summed from the rows - `SAAS.md` §3.92.
 *
 * **A stats page whose first question is *how is the platform doing* had no answer to it.** Every
 * number here is already on a row below; what was missing was the sum, and the owner counted that
 * absence against the whole product.
 *
 * <p><b>Summed only over rows whose figures are real.</b> `PlatformStatsService` reports an
 * unreachable tenant on the row and leaves its figures at zero deliberately, so a dashboard stays
 * usable during an incident - which means adding those zeros would quietly understate the platform
 * during exactly the incident somebody opened this page for. The count of skipped rows travels with
 * the total so the figure can say what it excludes rather than looking complete.
 */
export function statsTotals(rows: readonly FranchiseStat[]): StatsTotals {
  const totals: StatsTotals = {
    ordersMonth: 0,
    revenueMonthCents: 0,
    totalCustomers: 0,
    unreadable: 0,
  }
  for (const row of rows) {
    if (!showsFigures(row)) {
      totals.unreadable += 1
      continue
    }
    totals.ordersMonth += row.ordersMonth
    totals.revenueMonthCents += row.revenueMonthCents
    totals.totalCustomers += row.totalCustomers
  }
  return totals
}
